import { useEffect, useState } from "react";
import { useParams, useNavigate, NavLink, Outlet } from "react-router-dom";
import { ArrowLeft, LogOut, Loader2, FileText, Music, LayoutList, Zap, BarChart3, Users, Mail, BellRing } from "lucide-react";
import { getProject } from "./adminApi";
import { useAdminAuth } from "./AdminAuth";

const CONTENT = [
  { to: "release", label: "Release", icon: FileText },
  { to: "audio", label: "Audio", icon: Music },
  { to: "sections", label: "Sections", icon: LayoutList },
  { to: "trigger", label: "Trigger", icon: Zap },
];
const GROWTH = [
  { to: "analytics", label: "Analytics", icon: BarChart3 },
  { to: "fans", label: "Fans", icon: Users },
  { to: "opt-ins", label: "Opt-ins", icon: BellRing },
  { to: "broadcast", label: "Broadcast", icon: Mail },
];

function NavGroup({ label, items, slug }) {
  return (
    <div className="mb-6">
      <p className="font-mono-x text-[9px] tracking-[0.35em] text-[#555] uppercase px-3 mb-2">{label}</p>
      <div className="space-y-1">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={`/admin/projects/${slug}/${to}`}
            data-testid={`nav-${to}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? "bg-[#FF2A2A]/15 text-white border border-[#FF2A2A]/40" : "text-[#8a8a8a] hover:text-white hover:bg-white/5 border border-transparent"
              }`
            }
          >
            <Icon className="w-4 h-4 shrink-0" /> {label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export default function ProjectShell() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { logout } = useAdminAuth();
  const [project, setProject] = useState(null);

  useEffect(() => {
    getProject(slug).then(setProject).catch((e) => {
      if (e?.response?.status === 401) navigate("/admin/login");
      else navigate("/admin/projects");
    });
  }, [slug]);

  return (
    <div className="min-h-[100dvh] bg-[#050505] text-white" data-testid="project-shell">
      <header className="border-b border-white/10 px-4 sm:px-8 py-4 flex items-center justify-between sticky top-0 bg-[#050505]/90 backdrop-blur z-20">
        <button onClick={() => navigate("/admin/projects")} data-testid="back-to-projects"
          className="flex items-center gap-2 font-mono-x text-[11px] tracking-widest uppercase text-[#737373] hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" /> Projects
        </button>
        <div className="text-center">
          <p className="font-mono-x text-[10px] tracking-[0.3em] text-[#737373] uppercase">{project?.artist || "…"}</p>
          <h1 className="font-display text-lg font-bold tracking-tight">{project?.name || ""}</h1>
        </div>
        <button data-testid="admin-logout" onClick={async () => { await logout(); navigate("/admin/login"); }}
          className="text-[#737373] hover:text-white transition-colors"><LogOut className="w-4 h-4" /></button>
      </header>

      <div className="flex flex-col md:flex-row max-w-6xl mx-auto">
        {/* nav */}
        <aside className="md:w-56 shrink-0 border-b md:border-b-0 md:border-r border-white/10 px-3 py-4 md:py-8">
          <NavGroup label="Content" items={CONTENT} slug={slug} />
          <NavGroup label="Growth" items={GROWTH} slug={slug} />
        </aside>
        {/* content */}
        <main className="flex-1 min-w-0 px-4 sm:px-8 py-6 sm:py-8">
          {project === null ? (
            <div className="flex items-center gap-2 text-[#737373]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (
            <Outlet context={{ project }} />
          )}
        </main>
      </div>
    </div>
  );
}
